/**
 * settings 命名空间字典（i18n 分区）
 *
 * 从原 translations.ts 主字典按 key 首个点分段拆出；本文件内 key 不带命名空间前缀，
 * 调用点写 t('settings:key')。字段内容与拆分前逐字节一致。
 */

import type { TranslationDict } from '../types'

export const settingsZh: TranslationDict = {

  // ======================== 设置页面 / SettingsPage ========================
  'title': '设置',
  'adminButton': '管理面板',
  'quota': '额度',
  'profileTitle': '个人资料',
  'bioLabel': '简介',
  'bioPlaceholder': '介绍一下自己...',
  'statusTextLabel': '个性状态',
  'statusTextPlaceholder': '输入状态，如忙碌中',
  'statusTextHint': '中文≤10字，英文≤30字符，展示在资料卡中',
  'quotaTitle': '额度',
  'aiQuota': 'AI 创建额度',
  'aiQuotaSection': 'AI 创建额度',
  'aiQuotaHint': '每创建一个 AI 消耗 1 额度',
  'apiCredit': 'API 调用余额',
  'apiCreditSection': 'API 调用余额',
  'apiCreditHint': '每次 LLM 调用从此余额扣除',
  'platformCredit': '平台赠送额度',
  'platformCreditHint': '由管理员赠送，优先消耗',
  'totalEffective': '总有效余额',
  'totalEffectiveHint': '赠送额度 + 购买余额',
  'fileQuota': '文件配额',
  'fileQuotaHint': '可上传文件的总大小上限',
  'apiConfig': 'API 提供商配置',
  'apiConfigTitle': 'API 提供商配置',
  'apiConfigHint': '以下为全局默认，所有 AI 共用；不填就走平台提供的通道',
  'currentPreset': '当前预设',
  'baseUrl': '模型服务地址（Base URL）',
  'baseUrlPlaceholder': '例: https://api.deepseek.com',
  'baseUrlDesc': '这一栏填「模型服务商」的地址，不是你电脑的地址。想用平台提供的额度：保持默认或清空即可，不用自己申请 Key。想用自己的 Key：在上方选厂商预设，或填该厂商的地址（DeepSeek 是 https://api.deepseek.com）。地址末尾不用再写 /v1/chat/completions。',
  'baseUrlWarnLocal': '这是本机或局域网地址，服务器访问不到它——保存后 AI 会整轮发不出消息。要么填公网能访问的地址，要么把这里清空走平台通道。',
  'apiKey': 'API Key',
  'apiKeyPlaceholder': '输入 API Key，如 sk-xxxxxxxx',
  'apiKeyPlaceholderSet': '留空表示不修改（已设置）',
  'apiKeySet': '已设置 API Key',
  'apiKeyNotSet': '尚未设置 API Key（不填也能用平台额度）',
  'testConnection': '测试连接',
  'testing': '测试中...',
  'testSuccess': '连接成功',
  'testFailed': '连接失败',
  'perAgentTitle': '单 AI 配置（设置独立 API）',
  'perAgentHint': '（为特定 AI 设置独立 API，覆盖全局）',
  'noAgentsForApi': '暂无 AI，创建 AI 后可为每个 AI 配置独立 API',
  'agentBaseUrlPlaceholder': '例: https://api.deepseek.com（留空则继承全局）',
  'agentApiKeyPlaceholder': '例: sk-xxxxxxxx（留空则继承全局）',
  'agentApiSaving': '保存中...',
  'independentApiBadge': '独立 API',
  'inheritGlobalBadge': '继承全局',
  'timezone': '时区',
  'timezoneSection': '时区',
  'timezoneDesc': '消息时间将按此时区显示',
  'currentTimestamp': '当前:',
  'language': '语言',
  'languageSection': '语言',
  'languageDesc': '界面和 AI 系统提示词将使用所选语言',
  'chinese': '中文（简体）',
  'english': 'English',
  'japanese': '日本語',
  'chatStyle': '聊天样式',
  'chatStyleSection': '聊天样式',
  'chatStyleDesc': '控制群聊和私信界面中消息气泡的间距与布局风格',
  'cozyMode': '舒适模式',
  'cozyModeDesc': '气泡间距宽松，头像较大，适合长时间阅读',
  'compactMode': '紧凑模式',
  'compactModeDesc': '气泡排列紧密，信息密度高，适合快速浏览',
  'strategy': '策略模式',
  'strategySection': '策略模式',
  'autoApproveTimeout': '自动审批超时（秒）:',
  'autoApproveDefault': '默认同意向量加速申请',
  'appearance': '外观',
  'appearanceSection': '外观',
  'darkMode': '深色模式',
  'darkModeDesc': '当前：深色模式（深邃紫金）',
  'lightModeDesc': '当前：浅色模式',
  'instantApplyBadge': '即时生效',
  'themeCustom': '主题定制',
  'saved': '已保存',
  'enable': '启用',
  'presetProvider': '厂商预设',
  'themeCustomDesc': '自定义界面主色：左侧手机壳实时预览，点选色块或输入 #RRGGBB 即时变化，保存后所有界面生效（日夜主题共用一套主色）。',
  'themeDesignPreview': '实时预览',
  'themeDesignPresets': '预设（点击整卡应用，仍需点「保存主题」生效）',
  'themeDesignSaveAsPreset': '存为我的预设',
  'themeDesignPresetName': '预设名称',
  'themeDesignPresetSaved': '已存为我的预设',
  'themeDesignPresetDeleted': '已删除该预设',
  'themeDesignPresetEmpty': '还没有我的预设——先调好配色，点「存为我的预设」保存多套方案随时切换',
  'themeDesignPresetApply': '应用此预设',
  'themeDesignDeletePreset': '删除该预设',
  'themeDesignPicker': '取色器',
  'themeDesignSkinNote': '已自动停用当前皮肤（皮肤会覆盖自选主色），自定义色现在生效',
  'themePresetPlatform': '平台默认（定稿版）',
  'themePresetAurora': '极光青碧',
  'saveTheme': '保存主题',
  'resetTheme': '恢复默认',
  'skinTitle': '皮肤',
  'skinDesc': '一键启用/停用皮肤插件，启用后即时生效（皮肤会覆盖自选主色，停用后自选色恢复）。',
  'skinOn': '启用皮肤',
  'skinOff': '停用皮肤',
  'skinAdminOff': '管理员已关闭',
  'skinInUse': '使用中',
  'skinMutualExclusive': '点哪张卡片就用哪套；「默认」为平台配色（可配合上方自选主色）。',
  'skinDefault': '默认',
  'skinDefaultDesc': '平台默认配色，不套用任何皮肤',
  'loadingPlugins': '加载中…',
  'noSkins': '暂无可用皮肤（管理员未开放或未安装）',
  'notifications': '桌面通知',
  'notificationsSection': '桌面通知',
  'notificationsDesc': '标签页未读数量指示器',
  'notificationsLabel': '标签页未读标记',
  'notificationsDetailDesc': '切换标签页时在标题栏显示未读消息数，有新消息时任务栏闪烁（免打扰的群和私信不计入）',
  'notificationsPopupLabel': '新消息弹窗',
  'notificationsPopupDesc': '网页右下角弹出新消息提醒：私聊、群消息、审批结果、群公告与系统通知（免打扰的会话不弹，正在看的会话不弹）',
  'emailVerifiedDesc': '你的邮箱已验证，可用于登录和找回密码。',
  'emailNotVerifiedDesc': '邮箱已绑定但尚未验证，请联系管理员完成验证。',
  'emailNotSetDesc': '未绑定邮箱。绑定后可使用邮箱登录，也可用于找回密码。',
  'removeEmailTitle': '解绑邮箱',
  'removeEmailDesc': '解绑后你将无法使用邮箱登录和找回密码。确定要解绑吗？',
  'save': '保存设置',
  'saving': '保存中...',
  'saveSuccess': '设置已保存',
  'saveHint': '标有「即时生效」的选项修改后立即应用，无需点击保存。其余选项需点击保存后生效。',
  'unsaved': '有未保存的更改',
  'unsavedTitle': '未保存的修改',
  'unsavedDesc': '你有尚未保存的设置修改。离开此页面将丢失这些更改。确定要离开吗？',
  'discardChanges': '放弃修改',
  'continueEditing': '继续编辑',
  'catAccount': '账户',
  'catApi': 'API',
  'catPrefs': '偏好',
  'catBehavior': '行为',
  'catDesktop': '桌面端',
  'desktopSection': '桌面端设置',
  'desktopSectionDesc': '桌面端专属功能（Tauri 客户端），Web 端不显示。',
  'autoStart': '开机自启动',
  'autoStartDesc': '系统启动时自动启动 Copree 桌面端',
  'dataLocation': '数据存储位置',
  'dataLocationDesc': '聊天记录和文件缓存的存储路径',
  'clearCache': '清理缓存',
  'clearCacheDesc': '删除本地缓存文件，释放磁盘空间',
  'clearCacheBtn': '清理缓存',
  'clearCacheSuccess': '缓存已清理',
  'localModelEntry': '本地模型管理',
  'localModelEntryDesc': '管理本地运行的 AI 模型（Ollama）',
  'instanceSetupEntry': '实例连接配置',
  'instanceSetupEntryDesc': '修改 Copree 服务器连接地址',
  'buildFactory': '桌面端构建工厂',
  'buildFactoryDesc': '安装编译环境后，可在本机生成专属安装包。',
  'buildFactoryStatus': '编译环境状态',
  'buildFactoryInstalled': '已安装',
  'buildFactoryNotInstalled': '未安装',
  'buildFactoryInstall': '安装编译环境',
  'buildFactoryInstalling': '安装中...',
  'buildFactoryViewDocs': '查看说明',
  'buildFactoryRequired': '所需：约 5-8GB 磁盘空间，4GB 内存',
  'buildFactoryBuildTarget': '生成目标',
  'buildFactoryBuild': '开始构建',
  'buildFactoryBuilding': '构建中...',
  'buildFactoryViewLog': '查看构建日志',
  'buildFactoryLastBuild': '上次构建',
  'buildFactoryCrossPlatformHint': '此平台不支持（需在对应系统上构建）',
  'buildFactoryDownloadLink': '下载链接',
  // UI 缩放 / Mermaid 图表
  'uiScale': 'UI 缩放',
  'uiScaleDesc': '调整界面文字和元素大小',
  'scaleSmall': '小',
  'scaleSmallDesc': '紧凑布局',
  'scaleMedium': '中',
  'scaleMediumDesc': '默认大小',
  'scaleLarge': '大',
  'scaleLargeDesc': '放大显示',
  'scaleXLarge': '超大',
  'scaleXLargeDesc': '超大显示',
  'mermaidTitle': 'Mermaid 图表',
  'mermaidCollapse': '默认折叠图表（点击展开）',
  'mermaidCollapseErrors': '渲染出错时也折叠',
  // API 设置里的模型覆盖与厂商预设
  'apiKeyGet': '获取 API Key →',
  'globalChatModelOverride': '全局默认聊天模型（覆盖）',
  'globalWorkModelOverride': '全局默认工作模型（覆盖）',
  'modelPlaceholderChat': '例如：mimo-v2.6-flash',
  'modelPlaceholderWork': '例如：mimo-v2.6-pro',
  'emptyUsesGlobalDefault': '留空则使用系统全局默认',
  'preferOwnKey': '优先使用本人 API Key',
  'preferOwnKeyDesc': '开启后优先使用自己的 API Key，再尝试系统额度',
  'providerPreset': '厂商预设',
}

export const settingsEn: TranslationDict = {
  'title': 'Settings',
  'adminButton': 'Admin Panel',
  'quota': 'Quota',
  'profileTitle': 'Profile',
  'bioLabel': 'Bio',
  'bioPlaceholder': 'Introduce yourself...',
  'statusTextLabel': 'Status',
  'statusTextPlaceholder': 'Set a status, e.g. "Busy"',
  'statusTextHint': 'Chinese ≤10 chars, English ≤30 chars, shown on profile card',
  'quotaTitle': 'Quota',
  'aiQuota': 'AI Creation Slots',
  'aiQuotaSection': 'AI Creation Slots',
  'aiQuotaHint': 'Each AI creation consumes 1 slot',
  'apiCredit': 'API Call Balance',
  'apiCreditSection': 'API Call Balance',
  'apiCreditHint': 'Each LLM call deducts from this balance',
  'platformCredit': 'Platform Gifted Credit',
  'platformCreditHint': 'Granted by admin, consumed first',
  'totalEffective': 'Total Effective Balance',
  'totalEffectiveHint': 'Gifted credit + purchased balance',
  'fileQuota': 'File Quota',
  'fileQuotaHint': 'Maximum total size of uploaded files',
  'apiConfig': 'API Provider Config',
  'apiConfigTitle': 'API Provider Config',
  'apiConfigHint': 'Global default, shared by all AI; leave blank to use the platform channel',
  'currentPreset': 'Current Preset',
  'baseUrl': 'Model service URL (Base URL)',
  'baseUrlPlaceholder': 'e.g. https://api.deepseek.com',
  'baseUrlDesc': 'The address of the model provider — not your own computer. To use platform credit, keep the default or clear this field; no key needed. To use your own key, pick a provider preset above or enter that provider\'s address (DeepSeek: https://api.deepseek.com). Do not append /v1/chat/completions.',
  'baseUrlWarnLocal': 'This is a local or LAN address; the server cannot reach it, so the AI will never be able to reply. Use a publicly reachable address, or clear this field to use the platform channel.',
  'apiKey': 'API Key',
  'apiKeyPlaceholder': 'Enter API Key, e.g. sk-xxxxxxxx',
  'apiKeyPlaceholderSet': 'Leave empty to keep current (already set)',
  'apiKeySet': 'API Key set',
  'apiKeyNotSet': 'API Key not set yet (platform credit works without one)',
  'testConnection': 'Test Connection',
  'testing': 'Testing...',
  'testSuccess': 'Connection OK',
  'testFailed': 'Connection Failed',
  'perAgentTitle': 'Per-Agent Config (set independent API)',
  'perAgentHint': 'Set independent API for specific AI, overriding global',
  'noAgentsForApi': 'No AI yet. Create an AI to configure per-agent API.',
  'agentBaseUrlPlaceholder': 'e.g. https://api.deepseek.com (leave blank to inherit global)',
  'agentApiKeyPlaceholder': 'e.g. sk-xxxxxxxx (leave blank to inherit global)',
  'agentApiSaving': 'Saving...',
  'independentApiBadge': 'Independent API',
  'inheritGlobalBadge': 'Inherited Global',
  'timezone': 'Timezone',
  'timezoneSection': 'Timezone',
  'timezoneDesc': 'Message timestamps will display in this timezone',
  'currentTimestamp': 'Current:',
  'language': 'Language',
  'languageSection': 'Language',
  'languageDesc': 'Interface and AI system prompts will use the selected language',
  'chinese': 'Chinese (Simplified)',
  'english': 'English',
  'japanese': 'Japanese',
  'chatStyle': 'Chat Style',
  'chatStyleSection': 'Chat Style',
  'chatStyleDesc': 'Controls spacing and layout of message bubbles in group chats and DMs',
  'cozyMode': 'Cozy',
  'cozyModeDesc': 'Relaxed bubble spacing, larger avatars',
  'compactMode': 'Compact',
  'compactModeDesc': 'Dense message layout, high information density',
  'strategy': 'Strategy',
  'strategySection': 'Strategy Mode',
  'autoApproveTimeout': 'Auto-approve Timeout (s):',
  'autoApproveDefault': 'Default approve vector acceleration',
  'appearance': 'Appearance',
  'appearanceSection': 'Appearance',
  'darkMode': 'Dark Mode',
  'darkModeDesc': 'Current: Dark Mode (Deep Purple Gold)',
  'lightModeDesc': 'Current: Light Mode',
  'instantApplyBadge': 'Instant Apply',
  'themeCustom': 'Theme Customizer',
  'saved': 'Saved',
  'enable': 'Enable',
  'presetProvider': 'Provider Presets',
  'themeCustomDesc': 'Customize the accent colors: live phone preview on the left — pick swatches or type #RRGGBB for instant feedback; save to apply everywhere (light/dark share one palette).',
  'themeDesignPreview': 'Live Preview',
  'themeDesignPresets': 'Presets (click a card to load it, then hit Save Theme)',
  'themeDesignSaveAsPreset': 'Save as My Preset',
  'themeDesignPresetName': 'Preset name',
  'themeDesignPresetSaved': 'Saved as my preset',
  'themeDesignPresetDeleted': 'Preset deleted',
  'themeDesignPresetEmpty': 'No presets yet — tune the colors, then Save as My Preset to keep multiple palettes',
  'themeDesignPresetApply': 'Apply this preset',
  'themeDesignDeletePreset': 'Delete this preset',
  'themeDesignPicker': 'Color picker',
  'themeDesignSkinNote': 'The active skin was disabled automatically (skins override custom colors); your custom colors are now live',
  'themePresetPlatform': 'Platform Default (Final)',
  'themePresetAurora': 'Aurora Teal',
  'saveTheme': 'Save Theme',
  'resetTheme': 'Reset Default',
  'skinTitle': 'Skins',
  'skinDesc': 'Enable/disable skin plugins with one tap; applies instantly (skins override your custom colors, restoring them when disabled).',
  'skinOn': 'Enable skin',
  'skinOff': 'Disable skin',
  'skinAdminOff': 'Disabled by admin',
  'skinInUse': 'In use',
  'skinMutualExclusive': 'Tap a card to apply that skin; "Default" restores the platform palette (combines with your custom colors above).',
  'skinDefault': 'Default',
  'skinDefaultDesc': 'Platform default palette, no skin applied',
  'loadingPlugins': 'Loading…',
  'noSkins': 'No skins available (none installed or disabled by admin)',
  'notifications': 'Desktop Notifications',
  'notificationsSection': 'Desktop Notifications',
  'notificationsDesc': 'Tab unread count indicator',
  'notificationsLabel': 'Tab Unread Badge',
  'notificationsDetailDesc': 'Shows unread count in tab title and flashes taskbar on new messages (muted chats excluded)',
  'notificationsPopupLabel': 'In-app message popups',
  'notificationsPopupDesc': 'Bottom-right toasts for DMs, group messages, approvals, announcements and system notices (muted or currently open chats excluded)',
  'emailVerifiedDesc': 'Your email is verified and can be used for login and password recovery.',
  'emailNotVerifiedDesc': 'Email is bound but not yet verified. Please contact the admin to complete verification.',
  'emailNotSetDesc': 'No email bound. Bind an email to enable email login and password recovery.',
  'removeEmailTitle': 'Unbind Email',
  'removeEmailDesc': 'You will no longer be able to use email login or password recovery. Are you sure?',
  'save': 'Save Settings',
  'saving': 'Saving...',
  'saveSuccess': 'Settings saved',
  'saveHint': 'Options marked "Instant Apply" take effect immediately. Others require clicking "Save" to apply.',
  'unsaved': 'Unsaved Changes',
  'unsavedTitle': 'Unsaved Changes',
  'unsavedDesc': 'You have unsaved changes. Leaving will discard them. Are you sure you want to leave?',
  'discardChanges': 'Discard Changes',
  'continueEditing': 'Continue Editing',
  'catAccount': 'Account',
  'catApi': 'API',
  'catPrefs': 'Preferences',
  'catBehavior': 'Behavior',
  'catDesktop': 'Desktop',
  'desktopSection': 'Desktop Settings',
  'desktopSectionDesc': 'Desktop app (Tauri) exclusive features, not available in Web.',
  'autoStart': 'Auto Start',
  'autoStartDesc': 'Launch Copree on system startup',
  'dataLocation': 'Data Storage Location',
  'dataLocationDesc': 'Path where chat history and file cache are stored',
  'clearCache': 'Clear Cache',
  'clearCacheDesc': 'Delete local cache files to free up disk space',
  'clearCacheBtn': 'Clear Cache',
  'clearCacheSuccess': 'Cache cleared successfully',
  'localModelEntry': 'Local Model Manager',
  'localModelEntryDesc': 'Manage locally running AI models (Ollama)',
  'instanceSetupEntry': 'Instance Connection',
  'instanceSetupEntryDesc': 'Change the Copree server connection address',
  'buildFactory': 'Desktop Build Factory',
  'buildFactoryDesc': 'After installing the build environment, you can generate custom installers on this machine.',
  'buildFactoryStatus': 'Build Environment Status',
  'buildFactoryInstalled': 'Installed',
  'buildFactoryNotInstalled': 'Not Installed',
  'buildFactoryInstall': 'Install Build Environment',
  'buildFactoryInstalling': 'Installing...',
  'buildFactoryViewDocs': 'View Instructions',
  'buildFactoryRequired': 'Required: ~5-8 GB disk space, 4 GB RAM',
  'buildFactoryBuildTarget': 'Build Target',
  'buildFactoryBuild': 'Start Build',
  'buildFactoryBuilding': 'Building...',
  'buildFactoryViewLog': 'View Build Log',
  'buildFactoryLastBuild': 'Last Build',
  'buildFactoryCrossPlatformHint': 'Not available on this platform (requires corresponding OS)',
  'buildFactoryDownloadLink': 'Download Link',
  // UI scale / Mermaid diagrams
  'uiScale': 'UI Scale',
  'uiScaleDesc': 'Adjust the size of interface text and elements',
  'scaleSmall': 'Small',
  'scaleSmallDesc': 'Compact layout',
  'scaleMedium': 'Medium',
  'scaleMediumDesc': 'Default size',
  'scaleLarge': 'Large',
  'scaleLargeDesc': 'Enlarged display',
  'scaleXLarge': 'Extra large',
  'scaleXLargeDesc': 'Maximum display size',
  'mermaidTitle': 'Mermaid Diagrams',
  'mermaidCollapse': 'Collapse diagrams by default (click to expand)',
  'mermaidCollapseErrors': 'Also collapse when rendering fails',
  // Model overrides and provider presets in the API section
  'apiKeyGet': 'Get an API Key →',
  'globalChatModelOverride': 'Global default chat model (override)',
  'globalWorkModelOverride': 'Global default work model (override)',
  'modelPlaceholderChat': 'e.g. mimo-v2.6-flash',
  'modelPlaceholderWork': 'e.g. mimo-v2.6-pro',
  'emptyUsesGlobalDefault': 'Leave empty to use the system default',
  'preferOwnKey': 'Prefer my own API Key',
  'preferOwnKeyDesc': 'When on, your own API Key is used first, then platform credit',
  'providerPreset': 'Provider presets',
}

export const settingsJa: TranslationDict = {
  'title': '設定',
  'adminButton': '管理パネル',
  'quota': 'クォータ',
  'profileTitle': 'プロフィール',
  'bioLabel': '自己紹介',
  'bioPlaceholder': '自己紹介をどうぞ...',
  'statusTextLabel': 'ステータス',
  'statusTextPlaceholder': '例: 取り込み中',
  'statusTextHint': '日本語10文字以内、英語30文字以内。プロフィールカードに表示',
  'quotaTitle': 'クォータ',
  'aiQuota': 'AI作成枠',
  'aiQuotaSection': 'AI作成枠',
  'aiQuotaHint': 'AIを1体作成するごとに1枠消費',
  'apiCredit': 'APIコール残高',
  'apiCreditSection': 'APIコール残高',
  'apiCreditHint': 'LLM呼び出しごとに残高から差し引かれます',
  'platformCredit': 'プラットフォーム付与クレジット',
  'platformCreditHint': '管理者から付与され、優先的に消費されます',
  'totalEffective': '有効残高合計',
  'totalEffectiveHint': '付与クレジット + 購入残高',
  'fileQuota': 'ファイル容量',
  'fileQuotaHint': 'アップロード可能なファイルの総サイズ上限',
  'apiConfig': 'API プロバイダ設定',
  'apiConfigTitle': 'API プロバイダ設定',
  'apiConfigHint': '以下はグローバルデフォルトです（全AIで共有）。空欄ならプラットフォームの経路を使います',
  'currentPreset': '現在のプリセット',
  'baseUrl': 'モデルサービスのURL（Base URL）',
  'baseUrlPlaceholder': '例: https://api.deepseek.com',
  'baseUrlDesc': 'ここには「モデル提供元」のURLを入れます。自分のPCのアドレスではありません。プラットフォームの枠を使うなら既定のまま、または空欄でOK（自分のKeyは不要）。自分のKeyを使うなら上でプリセットを選ぶか、その提供元のURLを入力してください（DeepSeek は https://api.deepseek.com）。末尾に /v1/chat/completions は不要です。',
  'baseUrlWarnLocal': 'これはローカル/社内ネットワークのアドレスで、サーバーから到達できません。このまま保存するとAIは一切返信できなくなります。公衆から到達できるURLを入れるか、空欄にしてプラットフォームの経路を使ってください。',
  'apiKey': 'API Key',
  'apiKeyPlaceholder': 'API Keyを入力（例: sk-xxxxxxxx）',
  'apiKeyPlaceholderSet': '変更しない場合は空欄（設定済み）',
  'apiKeySet': 'API Key 設定済み',
  'apiKeyNotSet': 'API Key 未設定（なくてもプラットフォームの枠で利用可）',
  'testConnection': '接続テスト',
  'testing': 'テスト中...',
  'testSuccess': '接続成功',
  'testFailed': '接続失敗',
  'perAgentTitle': 'AI個別設定（独立API）',
  'perAgentHint': '（特定のAIに独立したAPIを設定し、グローバルを上書き）',
  'noAgentsForApi': 'まだAIがありません。AIを作成すると個別APIを設定できます',
  'agentBaseUrlPlaceholder': '例: https://api.deepseek.com（空欄でグローバル継承）',
  'agentApiKeyPlaceholder': '例: sk-xxxxxxxx（空欄でグローバル継承）',
  'agentApiSaving': '保存中...',
  'independentApiBadge': '独立API',
  'inheritGlobalBadge': 'グローバル継承',
  'timezone': 'タイムゾーン',
  'timezoneSection': 'タイムゾーン',
  'timezoneDesc': 'メッセージの時刻がこのタイムゾーンで表示されます',
  'currentTimestamp': '現在:',
  'language': '言語',
  'languageSection': '言語',
  'languageDesc': 'インターフェースとAIのシステムプロンプトが選択された言語になります',
  'chinese': '中文（簡体字）',
  'english': 'English',
  'japanese': '日本語',
  'chatStyle': 'チャットスタイル',
  'chatStyleSection': 'チャットスタイル',
  'chatStyleDesc': 'グループとDMのメッセージバブルの余白やレイアウトを制御します',
  'cozyMode': 'ゆったり',
  'cozyModeDesc': 'バブルの間隔が広く、アバターが大きめ。長時間の閲覧に最適',
  'compactMode': 'コンパクト',
  'compactModeDesc': 'バブルが詰まった高密度表示。素早い閲覧向け',
  'strategy': '戦略モード',
  'strategySection': '戦略モード',
  'autoApproveTimeout': '自動承認タイムアウト（秒）:',
  'autoApproveDefault': 'デフォルトでベクター高速化申請を承認',
  'appearance': '外観',
  'appearanceSection': '外観',
  'darkMode': 'ダークモード',
  'darkModeDesc': '現在: ダークモード（ディープパープルゴールド）',
  'lightModeDesc': '現在: ライトモード',
  'instantApplyBadge': '即時反映',
  'themeCustom': 'テーマカスタマイズ',
  'saved': '保存しました',
  'enable': '有効化',
  'presetProvider': 'プロバイダープリセット',
  'themeCustomDesc': 'アクセントカラーをカスタマイズ：左のスマホプレビューでリアルタイム確認。スウォッチを選ぶか #RRGGBB を入力すると即時反映、保存すると全画面に適用（ライト/ダーク共通）。',
  'themeDesignPreview': 'ライブプレビュー',
  'themeDesignPresets': 'プリセット（カードをクリックで読み込み、「テーマを保存」で適用）',
  'themeDesignSaveAsPreset': 'マイプリセットとして保存',
  'themeDesignPresetName': 'プリセット名',
  'themeDesignPresetSaved': 'マイプリセットに保存しました',
  'themeDesignPresetDeleted': 'プリセットを削除しました',
  'themeDesignPresetEmpty': 'マイプリセットはまだありません — 配色を調整して「マイプリセットとして保存」で複数保存できます',
  'themeDesignPresetApply': 'このプリセットを適用',
  'themeDesignDeletePreset': 'このプリセットを削除',
  'themeDesignPicker': 'カラーピッカー',
  'themeDesignSkinNote': '有効中のスキンを自動的に無効化しました（スキンはカスタムカラーを上書きするため）。カスタムカラーが適用されています',
  'themePresetPlatform': 'プラットフォーム標準（決定版）',
  'themePresetAurora': 'オーロラティール',
  'saveTheme': 'テーマを保存',
  'resetTheme': 'デフォルトに戻す',
  'skinTitle': 'スキン',
  'skinDesc': 'スキンプラグインをワンタップで有効/無効にできます。有効にすると即時適用（スキンはカスタムカラーを上書き、無効にすると復元）。',
  'skinOn': 'スキンを有効化',
  'skinOff': 'スキンを無効化',
  'skinAdminOff': '管理者が無効化',
  'skinInUse': '使用中',
  'skinMutualExclusive': 'カードをタップしてスキンを適用。「デフォルト」はプラットフォーム配色に戻します（上のカスタムカラーと併用可）。',
  'skinDefault': 'デフォルト',
  'skinDefaultDesc': 'プラットフォーム標準配色、スキンなし',
  'loadingPlugins': '読み込み中…',
  'noSkins': '利用可能なスキンがありません（未インストールまたは管理者が無効化）',
  'notifications': 'デスクトップ通知',
  'notificationsSection': 'デスクトップ通知',
  'notificationsDesc': 'タブの未読数インジケーター',
  'notificationsLabel': 'タブ未読バッジ',
  'notificationsDetailDesc': 'タブ切り替え時に未読数を表示し、新着メッセージでタスクバーが点滅します（おやすみ中のグループ/DMは除く）',
  'notificationsPopupLabel': '新着メッセージのポップアップ',
  'notificationsPopupDesc': 'ページ右下に新着を表示します：DM・グループメッセージ・承認結果・お知らせ・システム通知（ミュート中や表示中の会話は除く）',
  'emailVerifiedDesc': 'メールアドレスは確認済みです。ログインやパスワードリセットに使用できます。',
  'emailNotVerifiedDesc': 'メールアドレスは連携されていますが未確認です。管理者に確認を依頼してください。',
  'emailNotSetDesc': 'メールアドレスが連携されていません。連携するとメールログインやパスワードリセットが可能です。',
  'removeEmailTitle': 'メール連携解除',
  'removeEmailDesc': '解除するとメールログインとパスワードリセットが使えなくなります。よろしいですか？',
  'save': '設定を保存',
  'saving': '保存中...',
  'saveSuccess': '設定を保存しました',
  'saveHint': '「即時反映」と表示されている項目は変更後すぐに適用されます。その他の項目は「保存」を押すと反映されます。',
  'unsaved': '未保存の変更があります',
  'unsavedTitle': '未保存の変更',
  'unsavedDesc': '保存されていない設定変更があります。このページを離れると変更は失われます。よろしいですか？',
  'discardChanges': '変更を破棄',
  'continueEditing': '編集を続ける',
  'catAccount': 'アカウント',
  'catApi': 'API',
  'catPrefs': '設定',
  'catBehavior': '動作',
  'catDesktop': 'デスクトップ',
  'desktopSection': 'デスクトップ設定',
  'desktopSectionDesc': 'デスクトップアプリ（Tauri）専用機能。Web版では表示されません。',
  'autoStart': '自動起動',
  'autoStartDesc': 'システム起動時にCopreeを自動起動します',
  'dataLocation': 'データ保存場所',
  'dataLocationDesc': 'チャット履歴とファイルキャッシュの保存パス',
  'clearCache': 'キャッシュクリア',
  'clearCacheDesc': 'ローカルキャッシュを削除してディスク容量を解放します',
  'clearCacheBtn': 'キャッシュをクリア',
  'clearCacheSuccess': 'キャッシュがクリアされました',
  'localModelEntry': 'ローカルモデル管理',
  'localModelEntryDesc': 'ローカルで実行中のAIモデル（Ollama）を管理',
  'instanceSetupEntry': 'インスタンス接続設定',
  'instanceSetupEntryDesc': 'Copreeサーバーの接続アドレスを変更',
  'buildFactory': 'デスクトップビルドファクトリー',
  'buildFactoryDesc': 'ビルド環境をインストール後、このマシンで専用インストーラーを生成できます。',
  'buildFactoryStatus': 'ビルド環境の状態',
  'buildFactoryInstalled': 'インストール済み',
  'buildFactoryNotInstalled': '未インストール',
  'buildFactoryInstall': 'ビルド環境をインストール',
  'buildFactoryInstalling': 'インストール中...',
  'buildFactoryViewDocs': '説明を表示',
  'buildFactoryRequired': '必要なもの：約5-8GBのディスク容量、4GBのメモリ',
  'buildFactoryBuildTarget': 'ビルドターゲット',
  'buildFactoryBuild': 'ビルド開始',
  'buildFactoryBuilding': 'ビルド中...',
  'buildFactoryViewLog': 'ビルドログを表示',
  'buildFactoryLastBuild': '前回のビルド',
  'buildFactoryCrossPlatformHint': 'このプラットフォームでは利用不可（対応するOSが必要です）',
  'buildFactoryDownloadLink': 'ダウンロードリンク',
  // UI スケール / Mermaid ダイアグラム
  'uiScale': 'UI スケール',
  'uiScaleDesc': 'UI の文字と要素の大きさを調整します',
  'scaleSmall': '小',
  'scaleSmallDesc': 'コンパクトなレイアウト',
  'scaleMedium': '中',
  'scaleMediumDesc': '既定の大きさ',
  'scaleLarge': '大',
  'scaleLargeDesc': '拡大表示',
  'scaleXLarge': '特大',
  'scaleXLargeDesc': '最大表示',
  'mermaidTitle': 'Mermaid ダイアグラム',
  'mermaidCollapse': '既定で図を折りたたむ（クリックで展開）',
  'mermaidCollapseErrors': 'レンダリング失敗時も折りたたむ',
  // API 設定のモデル上書きとプロバイダープリセット
  'apiKeyGet': 'API Key を取得 →',
  'globalChatModelOverride': 'グローバル既定チャットモデル（上書き）',
  'globalWorkModelOverride': 'グローバル既定ワークモデル（上書き）',
  'modelPlaceholderChat': '例：mimo-v2.6-flash',
  'modelPlaceholderWork': '例：mimo-v2.6-pro',
  'emptyUsesGlobalDefault': '空欄の場合はシステム既定を使用',
  'preferOwnKey': '自分の API Key を優先',
  'preferOwnKeyDesc': 'オンの場合、自分の API Key を先に使い、次にシステム枠を使用します',
  'providerPreset': 'プロバイダープリセット',
}
